/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { Atom, PrimitiveAtom, atom } from 'jotai';
import { userEmailSelector } from 'jotai/auth';
import { addOrUpdateEntityItem } from 'jotai/entity';
import { sanctioningBodyBySeriesSelector } from 'jotai/sanctioningBody';
import { activeSessionSelector } from 'jotai/session';
import { atomFamily, unwrap, useAtomCallback } from 'jotai/utils';
import { RunType, SanctioningBodyEnum, SeriesType, WorkspaceInfo } from 'models/viewModels';
import { useCallback } from 'react';
import { userPreferencesService } from 'services/userPreferences/UserPreferencesServices';
import { lockedWorkspaceService } from 'services/workspace/LockedWorkspaceService';
import { slugify } from 'services/workspace/Slugify';
import { systemWorkspaceService } from 'services/workspace/SystemWorkspaceService';
import { userWorkspaceService } from 'services/workspace/UserWorkspaceService';
import { WorkspaceGroup, workspaceService } from 'services/workspace/WorkspaceService';
import { v4 as uuidv4 } from 'uuid';
import { LockedWorkspaceId, TrackWorkspaceObject } from '.';
import { getBlankWorkspaceInfo } from './WorkspaceDefaultFactory';
import { WorkspaceObject } from './WorkspaceObject';

export const generateSlug = (name: string, userWorkspaces: WorkspaceInfo[]): string => {
  let slug = slugify(name);
  if (userWorkspaces.some((workspace) => workspace.slug === slug)) {
    slug = slugify(`${name}-${Date.now()}`);
  }
  return slug;
};

export const activeWorkspaceAtom = atom<WorkspaceGroup>({} as WorkspaceGroup);

const activeWorkspaceIdAtomValue = atom<string | undefined>(undefined);

export const activeWorkspaceIdAtom = atom<string | undefined, [string], Promise<void>>(
  (get) => get(activeWorkspaceIdAtomValue),
  async (_get, set, newValue: string) => {
    set(activeWorkspaceIdAtomValue, newValue);
    await workspaceService.refreshWorkspace();
  }
);

export const workspaceModifiedAtom = atom<boolean>(false);

export const fullscreenElementAtom = atom<'view' | Element | null>(null);

export const activeWorkspaceReadOnlyAtom = atom<boolean>((get) => {
  const owner = get(activeWorkspaceAtom).workspace.owner;
  const email = get(userEmailSelector);
  return !(owner && owner === email);
});

export const activeWorkspaceNameAtom = atom<string>((get) => get(activeWorkspaceAtom).workspace.name);

export const activeViewIdAtom = atom<string, [string], void>(
  (get) => {
    const currentWorkspace = get(activeWorkspaceAtom);
    if (Object.keys(currentWorkspace).length > 0) {
      return currentWorkspace.workspace.activeView;
    } else {
      return '';
    }
  },
  (get, set, newValue: string) => {
    const previousWorkspace = get(activeWorkspaceAtom);
    previousWorkspace.workspace.activeView = newValue;
    set(activeWorkspaceAtom, { ...previousWorkspace });
    workspaceService.updateWorkspace(previousWorkspace.workspace);
  }
);

export const hideFooterAtom = atom<boolean, [boolean], void>(
  (get) => {
    const currentWorkspace = get(activeWorkspaceAtom);
    if (Object.keys(currentWorkspace).length > 0) {
      return get(activeWorkspaceAtom).workspace.hideFooter;
    }
    return false;
  },
  (get, set, newValue: boolean) => {
    const previousWorkspace = get(activeWorkspaceAtom);
    previousWorkspace.workspace.hideFooter = newValue;
    set(activeWorkspaceAtom, { ...previousWorkspace });
  }
);

export const sessionSelectionOpenAtom = atom<boolean>(false);
export const workspaceLockedAtom = atom<boolean>(false);
export const manageWorkspacesOpenAtom = atom<boolean>(false);
export const saveWorkspaceAsOpenAtom = atom<boolean>(false);
export const changeWorkspaceOpenAtom = atom<boolean>(false);
export const syncWorkspaceOpenAtom = atom<boolean>(false);

const userWorkspaceIdsAtom = atom<string[]>([]);

userWorkspaceIdsAtom.onMount = (setAtom) => {
  const callAsync = async () => {
    const ids = await userWorkspaceService.getAllWorkspaceIds();
    setAtom(ids);
  };
  callAsync();
  //optional cleanup
  return () => {};
};

export const userWorkspacesAtomFamily = atomFamily<string, PrimitiveAtom<WorkspaceInfo>>((id: string) => {
  const userWorkspaceAtom = atom<WorkspaceInfo>(getBlankWorkspaceInfo(id));
  userWorkspaceAtom.onMount = (set) => {
    const getWorkspaceInfo = async () => {
      const value = await userWorkspaceService.getWorkspaceInfo(id);
      if (value) {
        set(value);
      }
    };
    getWorkspaceInfo();
    return () => {};
  };
  return userWorkspaceAtom;
});

export const allCurrentUserWorkspacesSelector = atom<WorkspaceInfo[]>((get) => {
  const userWorkspaceIds = get(userWorkspaceIdsAtom);
  const email = get(userEmailSelector);
  return userWorkspaceIds.reduce((previousValue, currentValue) => {
    const workspace = get(userWorkspacesAtomFamily(currentValue));
    if (!Object.hasOwn(workspace, 'shared')) {
      workspace.shared = false;
    }
    if (workspace.owner === email) {
      previousValue.push(workspace);
    }
    return previousValue;
  }, [] as WorkspaceInfo[]);
});

export const allAvailableWorkspacesSelector = atom<WorkspaceInfo[]>((get) => {
  const userWorkspaceIds = get(userWorkspaceIdsAtom);
  return userWorkspaceIds.reduce((previousValue, currentValue) => {
    const workspace = get(userWorkspacesAtomFamily(currentValue));
    previousValue.push(workspace);
    return previousValue;
  }, [] as WorkspaceInfo[]);
});

export const userWorkspaceSharedTeamIdsSelectorFamily = atomFamily<string, Atom<number[]>>((workspaceId: string) =>
  atom<number[]>((get) => {
    return get(userWorkspacesAtomFamily(workspaceId))?.sharedDomains || [];
  })
);

export const availableWorkspacesSelectorFamily = atomFamily<RunType, Atom<Promise<WorkspaceObject[]>>>(
  (sessionType: RunType) =>
    atom<Promise<WorkspaceObject[]>>(async (_get) => {
      const workspaces: WorkspaceObject[] = [];
      // System.
      workspaces.push(...systemWorkspaceService.getAvailableWorkspaces(sessionType));
      // Locked.
      workspaces.push(...lockedWorkspaceService.getAvailableWorkspaces(sessionType));
      // User.
      workspaces.push(...(await userWorkspaceService.getAvailableWorkspaces(sessionType)));
      return workspaces;
    })
);

export const avaialbleSharedWorkspacesSelectorFamily = atomFamily<RunType, Atom<Promise<WorkspaceObject[]>>>(
  (sessionType: RunType) =>
    atom<Promise<WorkspaceObject[]>>(async (get) => {
      return (await get(availableWorkspacesSelectorFamily(sessionType))).filter((w) => w.shared);
    })
);

export const availableWorkspacesByOwnerSelectorFamily = atomFamily<
  RunType,
  Atom<Promise<Map<string, WorkspaceObject[]>>>
>((sessionType: RunType) =>
  atom<Promise<Map<string, WorkspaceObject[]>>>(async (get) => {
    const workspaces = await get(availableWorkspacesSelectorFamily(sessionType));
    const map = new Map<string, WorkspaceObject[]>();
    workspaces.forEach((workspace) => {
      const arr = map.get(workspace.owner) || [];
      arr.push(workspace);
      map.set(workspace.owner, arr);
    });
    return map;
  })
);

export const availableWorkspacesSelector = atom<Promise<WorkspaceObject[]>>(async (get) => {
  const session = get(activeSessionSelector);
  return session ? await get(availableWorkspacesSelectorFamily(session.sessionType)) : [];
});

export const useSaveActiveWorkspace = () => {
  return useAtomCallback(
    useCallback(async () => {
      workspaceService.saveWorkspace();
    }, [])
  );
};

export const useSyncActiveWorkspace = () => {
  return useAtomCallback(async () => {
    workspaceService.syncWorkspace();
  });
};

export const useSaveActiveWorkspaceAs = () => {
  return useAtomCallback(
    useCallback(async (get, _set, name: string) => {
      const id = uuidv4();
      const slug = generateSlug(name, get(allCurrentUserWorkspacesSelector));
      const owner: string = get(userEmailSelector) || '';
      const { status, workspaceSlug } = await workspaceService.saveWorkspaceAs(id, name, slug, owner);
      return status ? workspaceSlug : null;
    }, [])
  );
};

export const useUpdateUserWorkspaceName = () => {
  return useAtomCallback(
    useCallback(async (get, set, workspaceId: string, name: string) => {
      const workspace = await userWorkspaceService.getWorkspaceInfo(workspaceId);
      if (workspace) {
        workspace.name = name;
        workspace.slug = generateSlug(name, get(allCurrentUserWorkspacesSelector));
        const success = await userWorkspaceService.updateWorkspaceInfo(workspace);
        if (success) {
          set(userWorkspacesAtomFamily(workspaceId), workspace);
        }
      }
    }, [])
  );
};

export const useUpdateUserWorkspaceShared = () => {
  return useAtomCallback(
    useCallback(async (get, set, workspaceId: string, shared: boolean) => {
      const workspace = await userWorkspaceService.getWorkspaceInfo(workspaceId);
      if (workspace) {
        workspace.shared = shared;
        const success = await userWorkspaceService.updateWorkspaceInfo(workspace);
        if (success) {
          //Before in atom.ts we were updating the current workspace which was strange.
          if (workspaceId === get(activeWorkspaceIdAtom)) {
            const previousWorkspace = get(activeWorkspaceAtom);
            previousWorkspace.workspace.shared = shared;
            set(activeWorkspaceAtom, { ...previousWorkspace });
            await workspaceService.updateWorkspaceShared(workspace.shared, workspace.sharedDomains);
          }
          set(userWorkspacesAtomFamily(workspaceId), workspace);
        }
      }
    }, [])
  );
};

export const useUpdateUserWorkspaceSharedDomains = () => {
  return useAtomCallback(
    useCallback(async (get, set, workspaceId: string, sharedDomains: number[]) => {
      const workspace = await userWorkspaceService.getWorkspaceInfo(workspaceId);
      if (workspace) {
        workspace.sharedDomains = sharedDomains;
        const success = await userWorkspaceService.updateWorkspaceInfo(workspace);
        if (success) {
          //Before in atom.ts we were updating the current workspace which was strange.
          if (workspaceId === get(activeWorkspaceIdAtom)) {
            const previousWorkspace = get(activeWorkspaceAtom);
            previousWorkspace.workspace.sharedDomains = sharedDomains;
            set(activeWorkspaceAtom, { ...previousWorkspace });
            await workspaceService.updateWorkspaceShared(workspace.shared, workspace.sharedDomains);
          }
          set(userWorkspacesAtomFamily(workspaceId), workspace);
        }
      }
    }, [])
  );
};

export const useDeleteUserWorkspace = () => {
  return useAtomCallback(
    useCallback(async (get, set, workspaceId: string) => {
      const userWorkspaceIds = get(userWorkspaceIdsAtom);
      const success = await userWorkspaceService.deleteWorkspace(workspaceId);
      if (success) {
        set(userWorkspacesAtomFamily(workspaceId), getBlankWorkspaceInfo(workspaceId));
        set(
          userWorkspaceIdsAtom,
          userWorkspaceIds.filter((id) => id !== workspaceId)
        );
      }
      return success;
    }, [])
  );
};

const getUserLockedWorkspace = (lockedWorkspaceId: LockedWorkspaceId, email: string) => {
  return `${lockedWorkspaceId}-${email}`;
};

export const useGetLockedWorkspace = () => {
  return useAtomCallback(
    useCallback(async (get, _set, lockedWorkspaceId: LockedWorkspaceId) => {
      const email = get(userEmailSelector) || '';
      const workspaceId = getUserLockedWorkspace(lockedWorkspaceId, email);
      const allUserWorkspaceIds = get(userWorkspaceIdsAtom);
      if (!allUserWorkspaceIds.some((id) => id === workspaceId)) {
        const workspace = lockedWorkspaceService.getWorkspace(lockedWorkspaceId);
        if (workspace) {
          workspace.id = workspaceId;
          workspace.owner = email;
          workspace.slug = slugify(workspaceId);
          await userWorkspaceService.updateWorkspace(workspace);
        }
      }
      return workspaceId;
    }, [])
  );
};

export const trackMapSettingsAtomFamily = atomFamily<string | undefined, Atom<TrackWorkspaceObject | undefined>>(
  (trackId: string | undefined) =>
    atom<TrackWorkspaceObject | undefined>((get) => {
      const currentWorkspace = get(activeWorkspaceAtom);
      if (Object.keys(currentWorkspace).length > 0) {
        if (trackId) {
          return currentWorkspace.workspace.trackMapSettings.byId[trackId] || undefined;
        }
        return undefined;
      }
      return undefined;
    })
);

export const useUpdateTrackMapSettings = () => {
  return useAtomCallback(
    useCallback((get, set, trackMapSettings: TrackWorkspaceObject) => {
      const currentWorkspace = get(activeWorkspaceAtom);
      addOrUpdateEntityItem(currentWorkspace.workspace.trackMapSettings, trackMapSettings, trackMapSettings.trackId);
      set(activeWorkspaceAtom, { ...currentWorkspace });
    }, [])
  );
};

export const availableWorkspacesBySanctioningBodySelectorFamily = atomFamily<
  SanctioningBodyEnum,
  Atom<Promise<WorkspaceObject[]>>
>((sanctioningBody: SanctioningBodyEnum) =>
  atom<Promise<WorkspaceObject[]>>(async (_get) => {
    const workspaces: WorkspaceObject[] = [];
    // System.
    workspaces.push(...systemWorkspaceService.getAvailableSanctioningBodyWorkspaces(sanctioningBody));
    // Locked.
    workspaces.push(...lockedWorkspaceService.getAvailableSanctioningBodyWorkspaces(sanctioningBody));
    // User.
    workspaces.push(...(await userWorkspaceService.getAvailableSanctioningBodyWorkspaces(sanctioningBody)));
    return workspaces;
  })
);

export const availableWorkspaceInfoBySanctioningBodySelectorFamily = atomFamily<
  SanctioningBodyEnum,
  Atom<Promise<WorkspaceInfo[]>>
>((sanctioningBody: SanctioningBodyEnum) =>
  atom<Promise<WorkspaceInfo[]>>(async (get) => {
    const workspaces: WorkspaceInfo[] = [];
    // System.
    workspaces.push(...systemWorkspaceService.getAvailableSanctioningBodyWorkspacesInfo(sanctioningBody));
    // Locked.
    workspaces.push(...lockedWorkspaceService.getAvailableSanctioningBodyWorkspacesInfo(sanctioningBody));
    // User.
    workspaces.push(
      ...get(allAvailableWorkspacesSelector).filter((w) => w.sanctioningBody.toString() === sanctioningBody.toString())
    );
    return workspaces;
  })
);

export const availableWorkspacesByOwnerAndSanctioningBodySelectorFamily = atomFamily<
  SanctioningBodyEnum,
  Atom<Map<string, WorkspaceInfo[]> | undefined>
>((sanctioningBody: SanctioningBodyEnum) =>
  unwrap(
    atom<Promise<Map<string, WorkspaceInfo[]>>>(async (get) => {
      const map = new Map<string, WorkspaceInfo[]>();
      (await get(availableWorkspaceInfoBySanctioningBodySelectorFamily(sanctioningBody))).forEach((workspace) => {
        const arr = map.get(workspace.owner) || [];
        arr.push(workspace);
        map.set(workspace.owner, arr);
      });
      return map;
    })
  )
);

export const sanctioningBodyDefaultWorkspaceSelectorFamily = atomFamily<
  SanctioningBodyEnum,
  Atom<Promise<WorkspaceObject | undefined>>
>((sanctioningBody: SanctioningBodyEnum) =>
  atom<Promise<WorkspaceObject | undefined>>(async (_get) => {
    return await userPreferencesService.getSanctioningBodyDefaultWorkspace(sanctioningBody);
  })
);

export const availableWorkspaceInfoBySanctioningBodySelector = atom<Promise<WorkspaceInfo[]>>(async (get) => {
  const session = get(activeSessionSelector);
  if (session && session.seriesId !== SeriesType.UNKNOWN.toString()) {
    const sanctioningBody = await get(sanctioningBodyBySeriesSelector(session.seriesId));
    if (sanctioningBody.sanctioningBodyId) {
      return await get(
        availableWorkspaceInfoBySanctioningBodySelectorFamily(
          Number(sanctioningBody.sanctioningBodyId) as SanctioningBodyEnum
        )
      );
    }
  }
  return [];
});

export const availableWorkspacesByActiveSanctioningBodySelector = atom<Promise<WorkspaceObject[]>>(async (get) => {
  const session = get(activeSessionSelector);
  if (session && session.seriesId !== SeriesType.UNKNOWN.toString()) {
    const sanctioningBody = await get(sanctioningBodyBySeriesSelector(session.seriesId));
    if (sanctioningBody.sanctioningBodyId) {
      return await get(
        availableWorkspacesBySanctioningBodySelectorFamily(
          Number(sanctioningBody.sanctioningBodyId) as SanctioningBodyEnum
        )
      );
    }
  }
  return [];
});

//TODO: Update when we fix Admin Workspace Settings
export const availableSharedWorkspacesSelectorFamily = atomFamily<RunType, Atom<Promise<WorkspaceObject[]>>>(
  (sessionType: RunType) =>
    atom<Promise<WorkspaceObject[]>>(async (get) => {
      return (await get(availableWorkspacesSelectorFamily(sessionType))).filter((w) => w.shared);
    })
);

export const mobileHighlightMenuOpenAtom = atom<boolean>(false);

export const isMobileWorkspaceAtom = atom<boolean>(false);